import { useEffect, useState, useRef } from "react";
import socket from "../../../../config";

/**
 * AIGenerationProgress
 *
 * Displays real-time progress of an AI module generation job.
 *
 * Data sources (highest to lowest priority):
 *   1. Socket.io  — "aiGeneration:progress" / "aiGeneration:complete" / "aiGeneration:error"
 *   2. Polling    — GET /modules/ai-generation-status/:jobId every 5 s (fallback)
 *
 * Props:
 *   jobId             {string}   required
 *   moduleId          {string}   required
 *   moduleName        {string}
 *   estimatedSections {number}
 *   estimatedQuestions{number}
 *   onComplete        {fn(job)}  called once when status reaches a terminal state
 *   onViewModule      {fn}       called when user clicks "View / Edit Module"
 *   moduleImageUrl    {string}   module image (imageURL); fallback: images/icon.png
 */

const DEFAULT_MODULE_ICON = require("../../../../../images/icon.png");

const STATUS_LABELS = {
    pending: 'Initializing…',
    running: 'Generating…',
    completed: 'Completed',
    partial: 'Completed with warnings',
    failed: 'Failed',
};

const STATUS_COLORS = {
    pending: '#f59e0b',
    running: '#4c6ef5',
    completed: '#10b981',
    partial: '#f59e0b',
    failed: '#ef4444',
};

const isTerminal = (s) => s === 'completed' || s === 'partial' || s === 'failed';

const ProgressBar = ({ value, max, color }) => {
    const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
    return (
        <div style={{ height: 8, background: '#e9ecef', borderRadius: 99, overflow: 'hidden' }}>
            <div
                style={{
                    height: '100%',
                    width: `${pct}%`,
                    background: color,
                    borderRadius: 99,
                    transition: 'width 0.6s ease',
                }}
            />
        </div>
    );
};

const AIGenerationProgress = ({
    jobId,
    moduleId,
    moduleName,
    moduleImageUrl,
    estimatedSections = 0,
    estimatedQuestions = 0,
    onComplete,
    onViewModule,
}) => {
    const [job, setJob] = useState(null);
    const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
    const completedFiredRef = useRef(false);

    const trimmedImageUrl = moduleImageUrl && String(moduleImageUrl).trim();
    const avatarSrc = !trimmedImageUrl || avatarLoadFailed ? DEFAULT_MODULE_ICON : trimmedImageUrl;

    // ── Merge incoming data into job state ──────────────────────────────────
    const mergeJob = (patch) =>
        setJob((prev) => ({ ...(prev || {}), ...patch }));

    // ── Socket.io listeners ─────────────────────────────────────────────────
    useEffect(() => {
        if (!jobId || !socket) return;

        const onProgress = (data) => {
            if (data.jobId !== jobId) return;
            mergeJob({ status: 'running', progress: data });
        };
        const onDone = (data) => {
            if (data.jobId !== jobId) return;
            mergeJob({ ...data });
        };
        const onError = (data) => {
            if (data.jobId !== jobId) return;
            mergeJob({ status: 'failed', error: data.error });
        };

        socket.on('aiGeneration:progress', onProgress);
        socket.on('aiGeneration:complete', onDone);
        socket.on('aiGeneration:error', onError);

        return () => {
            socket.off('aiGeneration:progress', onProgress);
            socket.off('aiGeneration:complete', onDone);
            socket.off('aiGeneration:error', onError);
        };
    }, [jobId]);


    useEffect(() => {
        setAvatarLoadFailed(false);
    }, [trimmedImageUrl]);

    // ── Fire onComplete callback once on terminal state ─────────────────────
    useEffect(() => {
        if (job && isTerminal(job.status) && !completedFiredRef.current) {
            completedFiredRef.current = true;
            if (onComplete) onComplete(job);
        }
    }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Derived values ──────────────────────────────────────────────────────
    const status = job?.status || 'pending';
    const progress = job?.progress || {};
    const result = job?.result || {};

    const sectionsCreated = progress.sectionsCreated ?? result.sectionsCreated ?? 0;
    const questionsCreated = progress.questionsCreated ?? result.questionsCreated ?? 0;
    const completedCombos = progress.completedCombos ?? 0;
    const totalCombos = progress.totalCombos ?? 9;
    const statusColor = STATUS_COLORS[status];

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <div style={{ padding: '2rem', maxWidth: '680px', margin: '0 auto' }}>
            <div className="bg1 shadow br10px p2rem">

                {/* Header ─────────────────────────────────────────────────── */}
                <div className="df aic mb2rem" style={{ gap: '1rem' }}>
                    <div
                        style={{
                            position: 'relative',
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            flexShrink: 0,
                            overflow: 'hidden',
                            boxShadow: `0 0 0 2px ${statusColor}33`,
                        }}
                    >
                        <img
                            alt=""
                            src={avatarSrc}
                            onError={() => setAvatarLoadFailed(true)}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                                background: statusColor + '18',
                            }}
                        />
                        {(status === 'running' || status === 'pending') && (
                            <div
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'rgba(0,0,0,0.4)',
                                }}
                            >
                                <div className="loader" style={{ width: 22, height: 22 }} />
                            </div>
                        )}
                        {status === 'completed' && (
                            <div
                                aria-hidden
                                style={{
                                    position: 'absolute',
                                    right: -1,
                                    bottom: -1,
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    background: '#10b981',
                                    color: '#fff',
                                    fontSize: '0.65rem',
                                    fontWeight: 800,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                                }}
                            >
                                ✓
                            </div>
                        )}
                        {status === 'partial' && (
                            <div
                                aria-hidden
                                style={{
                                    position: 'absolute',
                                    right: -1,
                                    bottom: -1,
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    background: '#f59e0b',
                                    color: '#fff',
                                    fontSize: '0.65rem',
                                    fontWeight: 800,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                                }}
                            >
                                ⚠
                            </div>
                        )}
                        {status === 'failed' && (
                            <div
                                aria-hidden
                                style={{
                                    position: 'absolute',
                                    right: -1,
                                    bottom: -1,
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    background: '#ef4444',
                                    color: '#fff',
                                    fontSize: '0.65rem',
                                    fontWeight: 800,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                                }}
                            >
                                ✗
                            </div>
                        )}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h2 className="fs1-25rem fw600 c15 m0">AI Content Generation</h2>
                        {moduleName && (
                            <p className="fs0-85rem c15 m0" style={{ opacity: 0.6 }}>{moduleName}</p>
                        )}
                    </div>

                    <span
                        style={{
                            background: statusColor + '18',
                            color: statusColor,
                            padding: '0.3rem 0.9rem',
                            borderRadius: '99px',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {STATUS_LABELS[status]}
                    </span>
                </div>

                {/* Current activity banner ──────────────────────────────── */}
                {(status === 'running' || status === 'pending') && progress.currentCombo && (
                    <div
                        className="mb1-5rem p0-75rem br8px"
                        style={{ background: '#eef2ff', border: '1px solid #c7d2fe' }}
                    >
                        <p className="fs0-85rem fw500 m0" style={{ color: '#4c6ef5' }}>
                            {progress.step === 'section_generation'
                                ? 'Generating sections for'
                                : 'Generating questions for'}{' '}
                            <strong>{progress.currentCombo}</strong>
                            {progress.currentSection && (
                                <span style={{ color: '#374151', fontWeight: 400 }}>
                                    {' '}› {progress.currentSection}
                                </span>
                            )}
                        </p>
                    </div>
                )}

                {/* Error message ───────────────────────────────────────── */}
                {status === 'failed' && job?.error && (
                    <div
                        className="mb1-5rem p0-75rem br8px"
                        style={{ background: '#fef2f2', border: '1px solid #fca5a5' }}
                    >
                        <p className="fs0-85rem m0" style={{ color: '#dc2626' }}>{job.error}</p>
                    </div>
                )}

                {/* Progress bars ───────────────────────────────────────── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                    {/* Combinations */}
                    <div>
                        <div className="df jcsb aic mb0-25rem">
                            <span className="fs0-8rem fw600 c15">Objective × Tier combinations</span>
                            <span className="fs0-8rem c15" style={{ opacity: 0.55 }}>
                                {completedCombos} / {totalCombos}
                            </span>
                        </div>
                        <ProgressBar value={completedCombos} max={totalCombos} color={statusColor} />
                    </div>

                    {/* Sections */}
                    <div>
                        <div className="df jcsb aic mb0-25rem">
                            <span className="fs0-8rem fw600 c15">Sections created</span>
                            <span className="fs0-8rem c15" style={{ opacity: 0.55 }}>
                                {sectionsCreated}{estimatedSections ? ` / ~${estimatedSections}` : ''}
                            </span>
                        </div>
                        <ProgressBar value={sectionsCreated} max={estimatedSections || sectionsCreated || 1} color="#8b5cf6" />
                    </div>

                    {/* Questions */}
                    <div>
                        <div className="df jcsb aic mb0-25rem">
                            <span className="fs0-8rem fw600 c15">Questions created</span>
                            <span className="fs0-8rem c15" style={{ opacity: 0.55 }}>
                                {questionsCreated}{estimatedQuestions ? ` / ~${estimatedQuestions}` : ''}
                            </span>
                        </div>
                        <ProgressBar value={questionsCreated} max={estimatedQuestions || questionsCreated || 1} color="#10b981" />
                    </div>
                </div>

                {/* Partial failure note ────────────────────────────────── */}
                {status === 'partial' && (result.partialFailures || 0) > 0 && (
                    <div
                        className="p0-75rem br8px mb1-5rem"
                        style={{ background: '#fffbeb', border: '1px solid #fde68a' }}
                    >
                        <p className="fs0-85rem m0" style={{ color: '#92400e' }}>
                            {result.partialFailures} combination(s) had GPT errors and were skipped.
                            You can manually add sections for those combinations in the module editor.
                        </p>
                    </div>
                )}

                {/* Success summary ─────────────────────────────────────── */}
                {(status === 'completed' || status === 'partial') && (
                    <div
                        className="p0-75rem br8px mb1-5rem"
                        style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}
                    >
                        <p className="fs0-85rem m0 fw500" style={{ color: '#15803d' }}>
                            Generated {sectionsCreated} sections and {questionsCreated} questions successfully.
                        </p>
                    </div>
                )}

                {/* Actions ─────────────────────────────────────────────── */}
                {isTerminal(status) && onViewModule && (
                    <div className="df aic" style={{ gap: '1rem', marginTop: '0.5rem' }}>
                        <button
                            type="button"
                            onClick={onViewModule}
                            style={{
                                background: '#4c6ef5',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 8,
                                padding: '0.6rem 1.5rem',
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                cursor: 'pointer',
                            }}
                        >
                            Go to Modules List
                        </button>
                    </div>
                )}

                {/* In-progress note ────────────────────────────────────── */}
                {!isTerminal(status) && (
                    <p className="fs0-78rem c15 m0 mt1rem" style={{ opacity: 0.5 }}>
                        Generation runs in the background — you can safely navigate away and return later.
                    </p>
                )}
            </div>
        </div>
    );
};

export default AIGenerationProgress;
